import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Barcode from 'react-barcode';
import './panaGame.scss';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { SocketState } from '../context/SocketProvider';
import { AuthState } from '../context/AuthProvider';
import { selectPanaGames, selectPanaWallet, selectPanaCurrentDrawInfo } from '../redux/pana-slice/gameInfoPanaSlice';
import SuccessMessagePopup from '../comonComponent/success-message-popup/SuccessMessagePopup';
import ErrorMessagePopup from '../comonComponent/error-message-popup/ErrorMessagePopup';

/* ---- static config (functionality wired later) ---- */

const LEGEND = [
  { label: '10', color: '#ffff00' },
  { label: '20', color: '#0070c0' },
  { label: '50', color: '#92d050' },
  { label: '100', color: '#ff00ff' },
];

const DRAWS = [
  { title: 'Draw1', from: '10:00', to: '10:30', result: '667 -97- 368' },
  { title: 'Draw2', from: '11:00', to: '11:30', result: '460 -06- 448' },
  { title: 'Draw3', from: '12:00', to: '12:30', result: '579 -18- 440' },
  { title: 'Draw4', from: '01:00', to: '01:30', result: '334 -04- 266' },
  { title: 'Draw5', from: '02:00', to: '02:30', result: '389 -05- 348' },
];

const ACTIONS = [
  { label: 'Refresh', color: '#f2dede' },
  { label: 'Result', color: '#e6e0ef' },
  { label: 'Reprint', color: '#dff0d8' },
  { label: 'Account', color: '#fcf8e3' },
  { label: 'Cancel', color: '#dff0d8' },
  { label: 'WinRate', color: '#fff2cc' },
];

// backend game_type sent in VIEW_GAME_INFO (confirm exact string with the server)
const PANA_GAME_TYPE = 'play_matka';

const SINGLE_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const JODI_NUMBERS = Array.from({ length: 100 }, (_, i) => String(i).padStart(2, '0'));
const MIN_BET_POINTS = 10;

/* ===== PANA helpers (matka canonical: digits ascending, 0 ranked last) ===== */
const rank = (d) => (d === 0 ? 10 : d);
const canonicalPana = (digits) => [...digits].sort((a, b) => rank(a) - rank(b)).join('');

// classify a 3-digit pana by digit repetition
const panaType = (pana) => {
  const distinct = new Set(pana.split('')).size;
  return distinct === 3 ? 'SP' : distinct === 2 ? 'DP' : 'TP';
};

// all canonical panas, grouped: SP (120), DP (90), TP (10)
const ALL_PANAS = (() => {
  const groups = { SP: [], DP: [], TP: [] };
  for (let a = 1; a <= 10; a++)
    for (let b = a; b <= 10; b++)
      for (let c = b; c <= 10; c++) {
        const pana = `${a % 10}${b % 10}${c % 10}`;
        groups[panaType(pana)].push(pana);
      }
  return groups;
})();

// group panas by ANK (digit sum % 10), sorted ascending within each group
const ankOf = (pana) => pana.split('').reduce((s, d) => s + Number(d), 0) % 10;
const groupByAnk = (list) => {
  const g = {};
  for (let a = 0; a < 10; a++) g[a] = [];
  list.forEach((p) => g[ankOf(p)].push(p));
  Object.values(g).forEach((arr) => arr.sort((a, b) => Number(a) - Number(b)));
  return g;
};
const SP_GROUPS = groupByAnk(ALL_PANAS.SP);                         // single panas (12 per ank)
const DP_GROUPS = groupByAnk(ALL_PANAS.DP);                         // double panas only (TP has its own radio)
const ANK_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

// Pana family map (from reference). Selecting a family key adds exactly its array.
const PANA_FAMILY_MAP = {
  '111': ['111', '116', '166', '666'],
  '112': ['112', '117', '126', '167', '266', '667'],
  '113': ['113', '118', '136', '168', '366', '668'],
  '114': ['114', '119', '146', '169', '466', '669'],
  '115': ['110', '115', '156', '160', '566', '660'],
  '122': ['122', '127', '177', '226', '267', '677'],
  '123': ['123', '128', '137', '178', '236', '268', '367', '678'],
  '124': ['124', '129', '147', '179', '246', '269', '467', '679'],
  '125': ['120', '125', '157', '170', '256', '260', '567', '670'],
  '133': ['133', '138', '188', '336', '368', '688'],
  '134': ['134', '139', '148', '189', '346', '369', '468', '689'],
  '135': ['130', '135', '158', '180', '356', '360', '568', '680'],
  '144': ['144', '149', '199', '446', '469', '699'],
  '145': ['140', '145', '159', '190', '456', '460', '569', '690'],
  '155': ['100', '150', '155', '556', '560', '600'],
  '222': ['222', '227', '277', '777'],
  '223': ['223', '228', '237', '278', '377', '778'],
  '224': ['224', '229', '247', '279', '477', '779'],
  '225': ['220', '225', '257', '270', '577', '770'],
  '233': ['233', '238', '288', '337', '378', '788'],
  '234': ['234', '239', '248', '289', '347', '379', '478', '789'],
  '235': ['230', '235', '258', '280', '357', '370', '578', '780'],
  '244': ['244', '249', '299', '447', '479', '799'],
  '245': ['240', '245', '259', '290', '457', '470', '579', '790'],
  '255': ['200', '250', '255', '557', '570', '700'],
  '333': ['333', '338', '388', '888'],
  '334': ['334', '339', '348', '389', '488', '889'],
  '335': ['330', '335', '358', '380', '588', '880'],
  '344': ['344', '349', '399', '448', '489', '899'],
  '345': ['340', '345', '359', '390', '458', '480', '589', '890'],
  '355': ['300', '350', '355', '558', '580', '800'],
  '444': ['444', '449', '499', '999'],
  '445': ['440', '445', '459', '490', '599', '990'],
  '455': ['400', '450', '455', '559', '590', '900'],
  '555': ['000', '500', '550', '555'],
};

const PANA_FAMILY_KEYS = Object.keys(PANA_FAMILY_MAP);

// any pana -> its family key (direct key, or via reverse lookup of members)
const NUMBER_TO_FAMILY = (() => {
  const map = {};
  Object.entries(PANA_FAMILY_MAP).forEach(([key, members]) => {
    members.forEach((n) => { map[n] = key; });
  });
  return map;
})();

// members of a selected pana family (accepts a key or any member)
const fullFamily = (pana) => {
  const key = PANA_FAMILY_MAP[pana] ? pana : NUMBER_TO_FAMILY[pana];
  return PANA_FAMILY_MAP[key] || [];
};

// all k-length combinations (indices) of arr
const getCombinations = (arr, k) => {
  const res = [];
  const combine = (path, start) => {
    if (path.length === k) { res.push([...path]); return; }
    for (let i = start; i < arr.length; i++) combine([...path, arr[i]], i + 1);
  };
  combine([], 0);
  return res;
};

// SP MOTOR: all single panas (3 distinct digits) from the entered digit set
const spMotor = (input) => {
  const digits = [...new Set((input || '').split('').filter((d) => /\d/.test(d)))];
  if (digits.length < 3) return [];
  return [...new Set(getCombinations(digits, 3).map((c) => canonicalPana(c.map(Number))))];
};

// DP MOTOR: all double panas (exactly 2 distinct digits) from the entered digit set
const dpMotor = (input) => {
  const digits = [...new Set((input || '').split('').filter((d) => /\d/.test(d)))];
  const out = new Set();
  for (const a of digits)
    for (const b of digits)
      for (const c of digits)
        if (new Set([a, b, c]).size === 2) out.add(canonicalPana([a, b, c].map(Number)));
  return [...out];
};

const BOTTOM_BUTTONS = [
  { label: 'Total Amount', color: '#ffbf00' },
  { label: 'Claim', color: '#0070c0', textColor: '#fff' },
  { label: 'Print', color: '#ff00ff', textColor: '#fff' },
  { label: 'Bet', color: '#ffff00' },
  { label: 'Whatsapp', color: '#92d050' },
];

// label for each server card_details bucket on the printed ticket
const PRINT_LABELS = { single: 'SINGLE', jodi: 'JODI', single_pana: 'SP', double_pana: 'DP', triple_pana: 'TP' };

// build a printable receipt straight from a PLAY_MATKA_PLACE_BET response (one response = one draw)
const receiptFromResponse = (d) => ({
  cardDetails: d?.card_details || {},
  date: d?.deal_date,
  createdAt: d?.createdAt,
  time: d?.deal_time,
  mode: d?.mode,
  gameLabel: d?.is_advance ? 'ADVANCE' : (d?.game_id || ''),
  user: d?.user_name,
  totalPoint: d?.total_bet_amount,
  ticket_id: d?.ticket_id,
});

// Hidden container that renders one receipt block (header + bets + barcode) per placed draw
// and, once mounted, copies the whole thing into a single print window — so an advance bet
// prints all its draws on one page (like the 3D game). Driven by the `tickets` array.
function PanaPrintReceipts({ tickets, onPrinted }) {
  const containerRef = useRef(null);

  useEffect(() => {
    // console.log("tickets", tickets);
    if (!tickets || !tickets.length) return;
    const html = containerRef.current?.innerHTML || '';
    const w = window.open('', '_blank');
    if (!w) { onPrinted?.(); return; }
    w.document.open();
    w.document.write(`<html><head><title>Pana</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 0 8px; color: #000; font-weight: bold; font-size: 14px; }
        .rc { width: 250px; }
        .rh h2 { margin: 0; font-size: 14px; }
        .rh p { margin: 0; font-size: 10px; color: #555; }
        .tag { font-style: italic; font-size: 11px; color: #444; }
        p { margin: 2px 0; }
        .line { border-bottom: 1px dashed #000; margin: 6px 0; }
      </style>
    </head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); onPrinted?.(); }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  if (!tickets || !tickets.length) return null;

  return (
    <div ref={containerRef} style={{ display: 'none' }}>
      {tickets.map((t, ti) => {
        const cd = t.cardDetails || {};
        const spots = Object.values(cd).reduce((s, nums) => s + Object.keys(nums || {}).length, 0);
        return (
          <div key={ti}>
            <div className="rc">
              <div className="rh">
                <h2>Golden Lotto Pana</h2>
                <p>(Ticket valid for 10 Days)</p>
                {localStorage.getItem('is_gst') === 'true' && <p>GST No : {localStorage.getItem('gst')}</p>}
              </div>
              <p className="tag">Amusement Only, Pana Game</p>
              <p><strong>Game Date:</strong> {t.date}</p>
              <p><strong>Game Time:</strong> {t.time}{t.mode ? ` (${t.mode.toUpperCase()})` : ''}</p>
              <p><strong>Ticket Time:</strong> {t.createdAt}</p>
              <p><strong>Game ID:</strong> {t.gameLabel}</p>
              <p><strong>Operator ID:</strong> {t.user} ($)</p>
              <p><strong>Total Selected Spots:</strong> {spots}</p>
              <p><strong>Total Point:</strong> {t.totalPoint}</p>
              <div>
                {Object.entries(cd).map(([type, nums]) => {
                  const entries = Object.entries(nums || {});
                  const chunks = [];
                  for (let i = 0; i < entries.length; i += 3) chunks.push(entries.slice(i, i + 3));
                  return (
                    <div key={type} style={{ width: '250px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{PRINT_LABELS[type] || type}:</div>
                      {chunks.map((chunk, ci) => (
                        <div key={ci} style={{ fontWeight: 'bold', fontSize: '14px' }}>
                          {chunk.map(([num, pts], i) => (
                            <span key={i} style={{ marginRight: '10px' }}>{num}*{pts}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign: 'center', marginTop: '4px' }}>
                {t.ticket_id && <Barcode value={String(t.ticket_id)} width={1} height={40} displayValue={false} margin={0} />}
                <p style={{ marginTop: 4, textAlign: 'center' }}>{t.ticket_id}</p>
              </div>
            </div>
            <div className="line" />
          </div>
        );
      })}
    </div>
  );
}

const TIME_SLOTS = [
  ['10:00 AM', '10:30 AM'],
  ['11:00 AM', '11:30 AM'],
  ['12:00 PM', '12:30 PM'],
  ['01:00 PM', '01:30 PM'],
  ['02:00 PM', '02:30 PM'],
  ['03:00 PM', '03:30 PM'],
  ['04:00 PM', '04:30 PM'],
  ['05:00 PM', '05:30 PM'],
  ['06:00 PM', '06:30 PM'],
  ['07:00 PM', '07:30 PM'],
  ['08:00 PM', '08:30 PM'],
  ['09:00 PM', '09:30 PM'],
];

function LayoutPana() {
  const navigate = useNavigate();
  const { socket } = SocketState();
  const { userDetail } = AuthState();

  // print-on-bet support
  const printPendingRef = useRef(false);
  const defaultSlotRef = useRef(null); // current-draw default slot (for post-bet reset)
  const placementQueueRef = useRef([]); // remaining placements to emit (sent one after another)
  const currentPlacementRef = useRef(null); // the placement whose response we're awaiting
  const responsesLeftRef = useRef(0); // responses still expected for the current placement (1 per draw)
  const printAccumRef = useRef([]); // draw tickets collected across placements, printed as one page
  const [printTickets, setPrintTickets] = useState([]); // tickets handed to PanaPrintReceipts

  const [clock, setClock] = useState(moment().format('hh:mm:ss A'));
  const [panaMode, setPanaMode] = useState('SP'); // SP | DP | TP | FAM (single-select)
  const [motorTypes, setMotorTypes] = useState({ SP: true, DP: false }); // multi-select
  // game info from VIEW_GAME_INFO (play_matka)
  // game info now comes from Redux (handled centrally in AuthProvider)
  const games = useSelector(selectPanaGames);
  const wallet = useSelector(selectPanaWallet);
  const currentDrawInfo = useSelector(selectPanaCurrentDrawInfo);
  const drawEndUtc = currentDrawInfo?.end_utc_time || null;
  // selected draw slots to bet on: [{ gameId, mode: 'open'|'close', time }]; >1 => advance bet
  const [selectedSlots, setSelectedSlots] = useState([]);
  // advance-draw selected times (pana-local; persisted in localStorage, not redux)
  const [advanceTimes, setAdvanceTimes] = useState(() => JSON.parse(localStorage.getItem('pana_advance_times') || '[]'));
  // whatsapp bet popup
  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  // direct claim ticket input (no popup; auto-claims on scan like 3d)
  const [claimTicketId, setClaimTicketId] = useState('');

  const [single, setSingle] = useState({ num: '', pts: '10' });
  const [jodi, setJodi] = useState({ num: '', pts: '10' });
  const [panaInput, setPanaInput] = useState({ num: '', pts: '10' });
  const [panaSelected, setPanaSelected] = useState([]); // SP/DP multi-select picker
  const [panaSearch, setPanaSearch] = useState('');     // ank/number search in picker
  const [panaPickerOpen, setPanaPickerOpen] = useState(false); // dropdown open state
  const panaPickerRef = useRef(null);
  const [motorInput, setMotorInput] = useState({ num: '', pts: '10' });

  // chips: selected chip value (10 by default) — applied to every PTS input
  const [selectedChip, setSelectedChip] = useState(10);

  //popups open  
  const [showWinRate, setShowWinRate] = useState(false);

  // placed bets
  const [bets, setBets] = useState([]);
  const totalAmount = bets.reduce((sum, b) => sum + b.points, 0);

  // add bets, accumulating points on duplicates (same type + number), like the reference
  const addBets = (list) => {
    setBets((prev) => {
      const byKey = new Map(prev.map((b) => [`${b.type}-${b.number}`, { ...b }]));
      list.forEach((nb) => {
        const key = `${nb.type}-${nb.number}`;
        if (byKey.has(key)) byKey.get(key).points += nb.points;
        else byKey.set(key, { ...nb });
      });
      return [...byKey.values()];
    });
  };

  useEffect(() => {
    const t = setInterval(() => setClock(moment().format('hh:mm:ss A')), 1000);
    return () => clearInterval(t);
  }, []);

  // sync advance-draw selection (set by AdvanceDrawPana via localStorage + event)
  useEffect(() => {
    const sync = () => setAdvanceTimes(JSON.parse(localStorage.getItem('pana_advance_times') || '[]'));
    window.addEventListener('panaAdvanceUpdated', sync);
    return () => window.removeEventListener('panaAdvanceUpdated', sync);
  }, []);

  // drop any selected slot whose draw side has finished (disabled) so it isn't counted
  useEffect(() => {
    setSelectedSlots((prev) =>
      prev.filter((s) => {
        const g = games.find((x) => x.game_id === s.gameId);
        if (!g) return true;
        const done = g.game_state === 'completed';
        const state = g.draw_details?.[s.mode]?.state;
        return !(done || (!!state && state !== 'bet'));
      })
    );
  }, [games]);

  // top-bar keyboard shortcuts: F1 = Result, F2 = Account
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F1') { e.preventDefault(); navigate('result'); }
      else if (e.key === 'F2') { e.preventDefault(); navigate('account'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // On entering pana-game, fetch game info — but only once the session is set,
  // otherwise a refresh fires this before AppLunchDetails relogin and the server
  // replies "User session not set" (which disconnects the socket).
  useEffect(() => {
    localStorage.setItem('gameRoute', 'pana');
    if (!socket || !userDetail) return;
    socket.emit('req', {
      en: 'VIEW_GAME_INFO',
      data: { game_type: PANA_GAME_TYPE },
    });
  }, [socket, userDetail]);

  // Place-bet responses are handled one at a time: the jodi event is emitted first, and only
  // once its response arrives is the next queued event (the other cards) sent. Each placement
  // prints one slip per selected draw time (advance bets => multiple slips). After the last
  // placement responds we clear the local bet list.
  // (toast + wallet/game refresh are handled centrally in AuthProvider)
  useEffect(() => {
    if (!socket) return;
    const onRes = (res) => {
      if (res?.en !== 'PLAY_MATKA_PLACE_BET') return;
      if (!currentPlacementRef.current) return; // not one of our in-flight placements
      if (res.data?.ticket_id) localStorage.setItem('pana_last_ticket', res.data.ticket_id);

      // the server sends one response per draw (each with its own ticket_id / deal_time), so
      // each response is exactly one receipt.
      if (res.err) {
        // treat an error as ending the current placement (avoid waiting forever)
        responsesLeftRef.current = 0;
      } else {
        if (printPendingRef.current) printAccumRef.current.push(receiptFromResponse(res.data));
        responsesLeftRef.current -= 1;
        if (responsesLeftRef.current > 0) return; // more draws of this placement still coming
      }

      // current placement finished -> emit the next queued placement (jodi first, then others)
      if (placementQueueRef.current.length) {
        const next = placementQueueRef.current.shift();
        currentPlacementRef.current = next;
        responsesLeftRef.current = next.slots.length;
        socket.emit('req', { en: 'PLAY_MATKA_PLACE_BET', data: next.data });
        return;
      }

      // all placements done -> print every draw as one page + reset the slip
      currentPlacementRef.current = null;
      if (printPendingRef.current) {
        setPrintTickets(printAccumRef.current);
        printAccumRef.current = [];
        printPendingRef.current = false;
      }
      setBets([]);
      setAdvanceTimes([]);
      localStorage.removeItem('pana_advance_times');
      // reset selected draw times back to the current draw
      setSelectedSlots(defaultSlotRef.current ? [defaultSlotRef.current] : []);
    };
    socket.on('res', onRes);
    return () => socket.off('res', onRes);
  }, [socket]);

  // close the PANA picker dropdown when clicking outside it
  useEffect(() => {
    if (!panaPickerOpen) return;
    const onDocMouseDown = (e) => {
      if (panaPickerRef.current && !panaPickerRef.current.contains(e.target)) setPanaPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [panaPickerOpen]);

  // default-select the current draw (from current_draw_info) once data is in the store
  useEffect(() => {
    const cdi = currentDrawInfo;
    const running = games.find((g) => g.game_state === 'running') || games[0];
    const defaultSlot = cdi?.game_id && cdi?.time
      ? { gameId: cdi.game_id, mode: cdi.draw_type || 'open', time: cdi.time.trim() }
      : running
        ? { gameId: running.game_id, mode: 'open', time: (running.slot_time || '').split('-')[0].trim() }
        : null;
    defaultSlotRef.current = defaultSlot;
    if (defaultSlot) setSelectedSlots((prev) => (prev.length ? prev : [defaultSlot]));
  }, [currentDrawInfo, games]);

  // ---- SINGLE DIGIT logic ----
  const placeSingleBet = () => {
    const num = single.num;
    const points = Number(single.pts);
    if (num === '') {
      toast.error('Select a SINGLE number (0-9)');
      return;
    }
    if (!points || points < MIN_BET_POINTS) {
      toast.error(`Minimum bet is ${MIN_BET_POINTS} points`);
      return;
    }
    addBets([{ id: `${Date.now()}`, type: 'SI', number: num, points }]);
    setSingle({ num: '', pts: String(selectedChip) });
  };

  // ---- JODI logic (2-digit 00-99; only allowed on OPEN) ----
  const placeJodiBet = () => {
    if (selectedSlots.length > 0 && !selectedSlots.some((s) => s.mode === 'open')) {
      toast.error('Jodi can only be played on OPEN');
      return;
    }
    const raw = jodi.num.trim();
    const points = Number(jodi.pts);
    if (!/^\d{1,2}$/.test(raw) || Number(raw) > 99) {
      toast.error('Enter a JODI number between 00 and 99');
      return;
    }
    if (!points || points < MIN_BET_POINTS) {
      toast.error(`Minimum bet is ${MIN_BET_POINTS} points`);
      return;
    }
    const number = raw.padStart(2, '0');
    addBets([{ id: `${Date.now()}`, type: 'JO', number, points }]);
    setJodi({ num: '', pts: String(selectedChip) });
  };

  // ---- PANA logic (SP / DP / TP show that type only, FAM = play whole family) ----
  const panaOptions = panaMode === 'FAM' ? PANA_FAMILY_KEYS : ALL_PANAS[panaMode];

  // SP/DP use the ank-grouped, searchable, multi-checkbox picker
  const usesPicker = panaMode === 'SP' || panaMode === 'DP';
  const pickerGroups = panaMode === 'SP' ? SP_GROUPS : DP_GROUPS;
  // which ank-groups (and which numbers) to show for the current search
  const visibleGroups = (() => {
    const s = panaSearch.trim();
    const ankSearch = /^[0-9]$/.test(s); // single digit -> match the ank (group header)
    return ANK_ORDER.map((ank) => {
      let panas = pickerGroups[ank];
      if (s) {
        if (ankSearch) {
          if (Number(s) !== ank) panas = [];
        } else {
          panas = panas.filter((p) => p.includes(s));
        }
      }
      return { ank, panas };
    }).filter((g) => g.panas.length);
  })();
  const togglePana = (pana) => {
    // SP aur DP me direct submit
    if (panaMode === "SP" || panaMode === "DP") {
      if (!panaInput.pts || Number(panaInput.pts) <= 0) {
        toast.error("Enter points");
        return;
      }
      placePanaBet([pana]); // sirf selected pana bhejo
      return;
    }
    // FAM ya baaki mode ke liye old behavior
    setPanaSelected((prev) =>
      prev.includes(pana)
        ? prev.filter((x) => x !== pana)
        : [...prev, pana]
    );
  };
  const toggleAnkGroup = (panas) => {
    // SP & DP => Direct submit
    if (panaMode === "SP" || panaMode === "DP") {
      placePanaBet(panas);
      return;
    }
    // FAM => Old multi-select behavior
    setPanaSelected((prev) => {
      const allSelected = panas.every((p) => prev.includes(p));
      if (allSelected) {
        return prev.filter((p) => !panas.includes(p));
      }
      return [...new Set([...prev, ...panas])];
    });
  };


  const placePanaBet = (selectedPanas = null) => {
    const points = Number(panaInput.pts);
    if (!points || points < MIN_BET_POINTS) {
      toast.error(`Minimum bet is ${MIN_BET_POINTS} points`);
      return;
    }
    let panas;
    if (usesPicker) {

      panas = selectedPanas || panaSelected;

      if (!panas.length) {
        toast.error("Select PANA number(s)");
        return;
      }

    } else {
      const base = panaInput.num;
      if (!base) {
        toast.error('Select a PANA number');
        return;
      }
      panas = panaMode === 'FAM' ? fullFamily(base) : [base];
    }
    const stamp = Date.now();
    addBets(
      panas.map((p, i) => ({
        id: `${stamp}-${i}`,
        type: panaType(p),
        number: p,
        points,
      }))
    );
    setPanaSelected([]);
    setPanaSearch('');
    setPanaPickerOpen(false);
    setPanaInput({ num: '', pts: String(selectedChip) });
  };

  // ---- MOTOR logic (SP = single panas, DP = double panas, from a digit set) ----
  const placeMotorBet = () => {
    const points = Number(motorInput.pts);
    if (!motorTypes.SP && !motorTypes.DP) {
      toast.error('Select SP and/or DP for Motor');
      return;
    }
    const panas = [
      ...(motorTypes.SP ? spMotor(motorInput.num) : []),
      ...(motorTypes.DP ? dpMotor(motorInput.num) : []),
    ];
    if (!panas.length) {
      toast.error(motorTypes.SP ? 'SP Motor needs at least 3 different digits' : 'DP Motor needs at least 2 different digits');
      return;
    }
    if (!points || points < MIN_BET_POINTS) {
      toast.error(`Minimum bet is ${MIN_BET_POINTS} points`);
      return;
    }
    const stamp = Date.now();
    addBets(panas.map((p, i) => ({ id: `${stamp}-${i}`, type: panaType(p), number: p, points })));
    setMotorInput({ num: '', pts: String(selectedChip) });
  };

  const removeBet = (id) => setBets((prev) => prev.filter((b) => b.id !== id));

  const switchGame = (route) => {
    localStorage.setItem('gameRoute', route === '/2d' ? '2d' : route === '/3d' ? '3d' : route === '/1d' ? '1d' : route === '/titli' ? 'Titli' : 'pana');
    socket?.emit('req', { en: 'LOTTARY_CLOSE_GAME', data: {} });
    navigate(route);
  };

  // draw columns from the server's games (fall back to static demo when none yet)
  const fmt = (t) => (t ? moment(t.trim(), 'hh:mm:ss A').format('hh:mm') : '');
  // show the result as soon as the OPEN part (first segment of win_card) is declared
  const hasResult = (wc) => {
    const open = (wc || '').split('-')[0] || '';
    return !!open && !open.includes('*');
  };
  const draws = games.length
    ? games
      .map((g, i) => {
        const [from, to] = (g.slot_time || '').split('-');
        return {
          title: `Draw${i + 1}`,
          from: fmt(from),
          to: fmt(to),
          result: g.win_card || '',
          declared: hasResult(g.win_card),
          // sort key from the draw's open time (used to put the latest result first)
          sortKey: moment((from || '').trim(), ['hh:mm:ss A', 'hh:mm A']).valueOf() || 0,
        };
      })
      .filter((d) => d.declared) // only show draws whose results are available
      .sort((a, b) => b.sortKey - a.sortKey) // latest result first
    : DRAWS;

  const runningGame = games.find((g) => g.game_state === 'running') || games[0];
  // current draw from current_draw_info (live), falling back to the running game
  const currentDraw = currentDrawInfo
    ? `CR. DRAW ${fmt(currentDrawInfo.time)} ${(currentDrawInfo.draw_type || '').toUpperCase()}`
    : runningGame
      ? `CR. DRAW ${fmt((runningGame.slot_time || '').split('-')[0])} ${(runningGame.draw_details?.open?.state || '').toUpperCase()}`
      : 'CR. DRAW';

  // remaining time = current_draw_info.end_utc_time - now (ticks via `clock`)
  const remainTime = (() => {
    void clock; // re-evaluate every second
    if (!drawEndUtc) return '--:--';
    const diff = moment(drawEndUtc).diff(moment());
    if (diff <= 0) return '00:00';
    const d = moment.duration(diff);
    const h = Math.floor(d.asHours());
    const mm = String(d.minutes()).padStart(2, '0');
    const ss = String(d.seconds()).padStart(2, '0');
    return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
  })();

  // OPEN/CLOSE slot rows (from server games; static fallback before data arrives)
  const slotRows = games.length
    ? games.map((g) => {
      const [open, close] = (g.slot_time || '').split('-').map((t) => t.trim());
      const done = g.game_state === 'completed';
      // a draw side stops accepting bets once its state is no longer "bet"
      const openDone = done || (!!g.draw_details?.open?.state && g.draw_details.open.state !== 'bet');
      const closeDone = done || (!!g.draw_details?.close?.state && g.draw_details.close.state !== 'bet');
      return { gameId: g.game_id, open, close, openDone, closeDone };
    })
    : TIME_SLOTS.map(([open, close]) => ({ gameId: '', open, close, openDone: false, closeDone: false }));

  const slotKey = (s) => `${s.gameId}-${s.mode}-${s.time}`;
  const isSlotSelected = (s) => selectedSlots.some((x) => slotKey(x) === slotKey(s));
  const toggleSlot = (s) =>
    setSelectedSlots((prev) =>
      prev.some((x) => slotKey(x) === slotKey(s)) ? prev.filter((x) => slotKey(x) !== slotKey(s)) : [...prev, s]
    );
  // a selected slot whose draw side is finished is disabled -> excluded from the bet
  const isSlotDisabled = (s) => {
    const row = slotRows.find((r) => r.gameId === s.gameId && (s.mode === 'open' ? r.open === s.time : r.close === s.time));
    return row ? (s.mode === 'open' ? row.openDone : row.closeDone) : false;
  };
  const activeSlots = selectedSlots.filter((s) => !isSlotDisabled(s)); // only enabled times count
  const hasOpenSlot = activeSlots.some((s) => s.mode === 'open');
  // Jodi is OPEN-only -> blocked when no OPEN slot is active
  const isClose = activeSlots.length > 0 && !hasOpenSlot;
  // Total = base points * number of selected (enabled) times.
  // Jodi is OPEN-only, so it is only charged per OPEN draw; the other cards are charged
  // for every selected draw. (e.g. advance on OPEN+CLOSE -> jodi counts once, others twice.)
  const slotCount = activeSlots.length || 1;
  const openSlotCount = activeSlots.filter((s) => s.mode === 'open').length;
  const jodiPoints = bets.reduce((sum, b) => sum + (b.type === 'JO' ? b.points : 0), 0);
  const otherPoints = totalAmount - jodiPoints;
  const jodiMultiplier = activeSlots.length ? openSlotCount : 1;
  const grandTotal = otherPoints * slotCount + jodiPoints * jodiMultiplier;

  // group a list of placed bets into PLAY_MATKA_PLACE_BET card_details buckets
  const buildCard = (list) => {
    const bucket = { SI: 'single', JO: 'jodi', SP: 'single_pana', DP: 'double_pana', TP: 'triple_pana' };
    const cd = {};
    list.forEach((b) => {
      const key = bucket[b.type];
      if (!key) return;
      if (!cd[key]) cd[key] = {};
      cd[key][b.number] = (cd[key][b.number] || 0) + b.points;
    });
    return cd;
  };

  const placeMatkaBet = (withPrint = false, mobile = null) => {
    if (!bets.length) {
      toast.error('No bets added');
      return;
    }
    if (!activeSlots.length) {
      toast.error('Select a draw time (OPEN / CLOSE) first');
      return;
    }

    const fmtTime = (t) => moment(t, ['hh:mm:ss A', 'hh:mm A']).format('hh:mm:ss A');

    // build one placement (PLAY_MATKA_PLACE_BET payload + its slots). multiple slots => advance
    // bet: the server places the card on each selected time and returns one response per draw, so
    // `slots.length` tells us how many responses to expect. Each response is printed as its own
    // receipt straight from the server data.
    const makePlacement = (slots, cardBets) => {
      const card_details = buildCard(cardBets);
      if (!slots.length || !Object.keys(card_details).length) return null;
      const isAdvance = slots.length > 1;
      const primary = slots[0];
      const data = { card_details, advance_bet: isAdvance };
      if (isAdvance) {
        data.advance_bet_times = slots.map((s) => fmtTime(s.time));
      } else {
        data.time = fmtTime(primary.time);
        data.mode = primary.mode;
        data.game_id = primary.gameId;
      }
      if (mobile) data.mobile_number = mobile;
      return { data, slots };
    };

    // Jodi is played on its own event first, then the rest of the cards (single/pana/etc.).
    // Jodi is OPEN-only, so its event targets the OPEN draw(s); the other cards go on every
    // selected draw.
    const openSlots = activeSlots.filter((s) => s.mode === 'open');
    const jodiPlacement = makePlacement(openSlots, bets.filter((b) => b.type === 'JO'));
    const otherPlacement = makePlacement(activeSlots, bets.filter((b) => b.type !== 'JO'));

    const placements = [jodiPlacement, otherPlacement].filter(Boolean);
    if (!placements.length) {
      toast.error('No applicable bets (Jodi needs an OPEN slot)');
      return;
    }

    printPendingRef.current = withPrint;
    printAccumRef.current = []; // fresh receipt collection for this bet

    // emit the first placement (jodi) now; the rest are sent sequentially as each response
    // arrives (see the PLAY_MATKA_PLACE_BET handler above)
    currentPlacementRef.current = placements[0];
    responsesLeftRef.current = placements[0].slots.length; // one response per draw
    placementQueueRef.current = placements.slice(1);
    socket.emit('req', { en: 'PLAY_MATKA_PLACE_BET', data: placements[0].data });
  };

  // Refresh: full reset back to the initial clean state (bets, inputs, selections,
  // modes, chip, draw-time selection, claim input and the whatsapp popup)
  const resetGame = () => {
    setBets([]);
    setSingle({ num: '', pts: '10' });
    setJodi({ num: '', pts: '10' });
    setPanaInput({ num: '', pts: '10' });
    setPanaSelected([]);
    setPanaSearch('');
    setPanaPickerOpen(false);
    setPanaMode('SP');
    setMotorInput({ num: '', pts: '10' });
    setMotorTypes({ SP: true, DP: false });
    setSelectedChip(10);
    // back to just the current draw (default slot), like after a successful bet
    setSelectedSlots(defaultSlotRef.current ? [defaultSlotRef.current] : []);
    setAdvanceTimes([]);
    localStorage.removeItem('pana_advance_times');
    setClaimTicketId('');
    setShowWhatsapp(false);
    setMobileNumber('');
  };

  const onAction = (label) => {
    if (label === 'Refresh') resetGame();
    else if (label === 'Result') navigate('result');
    else if (label === 'Reprint') navigate('reprint/list');
    else if (label === 'Account') navigate('account');
    else if (label === 'Cancel') navigate('cancelCoupon');
    else if (label === 'WinRate') {
      console.log("WINRATE CLICK");
      setShowWinRate(true);
    }
  };

  // chip click -> set the PTS of ALL bet cards to the chip value
  const applyChip = (val) => {
    setSelectedChip(val);
    const setPts = (p) => ({ ...p, pts: String(val) });
    setSingle(setPts);
    setJodi(setPts);
    setPanaInput(setPts);
    setMotorInput(setPts);
  };

  const onBottom = (label) => {
    if (label === 'Bet') placeMatkaBet(false);
    else if (label === 'Print') placeMatkaBet(true);
    else if (label === 'Whatsapp') setShowWhatsapp(true);
    // 'Total Amount' is display-only; 'Claim' is a direct input (handled separately)
  };

  // claim a ticket directly (no popup) — emit on scan/Enter
  const claimTicket = (id) => {
    const ticket = (id ?? claimTicketId).trim();
    if (!ticket) return;
    socket.emit('req', { en: 'PLAY_MATKA_TICKET_CLAIM', data: { ticket_id: ticket } });
    setClaimTicketId('');
  };

  // auto-claim once a full ticket id is entered (e.g. barcode scan), like 3d
  useEffect(() => {
    if (claimTicketId.length >= 10) claimTicket(claimTicketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimTicketId]);

  return (
    <>
      <div className="spreadsheet-container">

        {/* ===== TOP ROW ===== */}
        <div className="top-row-grid">
          <div className="chips-col">
            {LEGEND.map((l) => (
              <button
                key={l.label}
                type="button"
                className={`ss-chip${selectedChip === Number(l.label) ? ' selected' : ''}`}
                style={{ background: l.color, color: (l.label === '100' || l.label === '20') ? '#fff' : '#000' }}
                onClick={() => applyChip(Number(l.label))}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="draws-col-grid">
            {draws.map((d) => (
              <div className="draw-slot" key={d.title}>
                <div className="slot-header">{d.title}</div>
                <div className="slot-times"><span>{d.from}</span><span>{d.to}</span></div>
                <div className="slot-results">{d.result}</div>
              </div>
            ))}
          </div>

          <div className="schedule-header-col">
            <div className="sch-hdr-open">OPEN</div>
            <div className="sch-hdr-close">CLOSE</div>
          </div>
        </div>

        {/* ===== MAIN ===== */}
        <div className="main-content-grid">

          {/* LEFT: betting forms */}
          <div className="left-betting-panel">
            <div className="old-result-chart-label" onClick={() => navigate('result')}>OLD Result Chart</div>

            {/* SINGLE */}
            <div className="ss-bet-card single-card">
              <div className="ss-bet-header">SINGLE</div>
              <form className="ss-bet-form" onSubmit={(e) => { e.preventDefault(); placeSingleBet(); }}>
                <div className="ss-form-row">
                  <div className="ss-input-group">
                    <span className="input-lbl">NUM</span>
                    <select value={single.num} onChange={(e) => setSingle({ ...single, num: e.target.value })}>
                      <option value=""></option>
                      {SINGLE_DIGITS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="ss-input-group">
                    <span className="input-lbl">PTS</span>
                    <input type="number" value={single.pts} onChange={(e) => setSingle({ ...single, pts: e.target.value })} />
                  </div>
                  <button type="submit" className="ss-btn-bet">BET</button>
                </div>
                <div className="single-sublabel">0 TO 9</div>
              </form>
            </div>

            {/* JODI */}
            <div className="ss-bet-card jodi-card">
              <div className="ss-bet-header">JODI</div>
              <form className="ss-bet-form" onSubmit={(e) => { e.preventDefault(); if (!isClose) placeJodiBet(); }}>
                <div className="ss-form-row">
                  <div className="ss-input-group">
                    <span className="input-lbl">NUM</span>
                    <select value={jodi.num} onChange={(e) => setJodi({ ...jodi, num: e.target.value })}>
                      <option value=""></option>
                      {JODI_NUMBERS.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="ss-input-group">
                    <span className="input-lbl">PTS</span>
                    <input type="number" value={jodi.pts} onChange={(e) => setJodi({ ...jodi, pts: e.target.value })} />
                  </div>
                  <button type="submit" className="ss-btn-bet" disabled={isClose} title={isClose ? 'Jodi is OPEN-only' : ''}>BET</button>
                </div>
              </form>
            </div>

            {/* PANA */}
            <div className="ss-bet-card pana-card">
              <div className="ss-bet-header">PANA</div>
              <form className="ss-bet-form" onSubmit={(e) => { e.preventDefault(); placePanaBet(); }}>
                <div className="ss-checkbox-row">
                  {['SP', 'DP', 'TP', 'FAM'].map((k) => (
                    <label key={k}>
                      <input type="radio" name="pana-type" checked={panaMode === k} onChange={() => { setPanaMode(k); setPanaInput({ num: '', pts: String(selectedChip) }); setPanaSelected([]); setPanaSearch(''); setPanaPickerOpen(false); }} /> {k}
                    </label>
                  ))}
                </div>

                {usesPicker ? (
                  <>
                    <div className={`pana-picker${panaPickerOpen ? ' open' : ''}`} ref={panaPickerRef}>
                      <div className="pana-picker-input">
                        <span className="input-lbl">NUM</span>
                        <input
                          type="text"
                          className="pana-picker-search"
                          placeholder="Search ank (e.g. 1) or number…"
                          value={panaSearch}
                          onFocus={() => setPanaPickerOpen(true)}
                          onChange={(e) => { setPanaSearch(e.target.value); setPanaPickerOpen(true); }}
                        />
                      </div>
                      {panaPickerOpen && (
                        <div className="pana-picker-list">
                          {visibleGroups.length === 0 && <div className="pana-picker-empty">No match</div>}
                          {visibleGroups.map(({ ank, panas }) => {
                            const allOn = panas.every((p) => panaSelected.includes(p));
                            return (
                              <div key={ank} className="pana-pick-group">
                                <label className="pana-pick-header">
                                  <input type="checkbox" checked={allOn} onChange={() => toggleAnkGroup(panas)} />
                                  <span>{ank}</span>
                                </label>
                                {panas.map((p) => (
                                  <label key={p} className="pana-pick-item">
                                    <input type="checkbox" checked={panaSelected.includes(p)} onChange={() => togglePana(p)} />
                                    <span>{p}</span>
                                  </label>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {panaSelected.length > 0 && (
                        <div className="pana-picker-count">{panaSelected.length} selected</div>
                      )}
                    </div>
                    <div className="ss-form-row motor-row">
                      <div className="ss-input-group">
                        <span className="input-lbl">PTS</span>
                        <input type="number" value={panaInput.pts} onChange={(e) => setPanaInput({ ...panaInput, pts: e.target.value })} />
                      </div>
                      <button type="submit" className="ss-btn-bet">BET</button>
                    </div>
                  </>
                ) : (
                  <div className="ss-form-row">
                    <div className="ss-input-group">
                      <span className="input-lbl">NUM</span>
                      <select value={panaInput.num} onChange={(e) => setPanaInput({ ...panaInput, num: e.target.value })}>
                        <option value=""></option>
                        {panaOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="ss-input-group">
                      <span className="input-lbl">PTS</span>
                      <input type="number" value={panaInput.pts} onChange={(e) => setPanaInput({ ...panaInput, pts: e.target.value })} />
                    </div>
                    <button type="submit" className="ss-btn-bet">BET</button>
                  </div>
                )}
              </form>
            </div>

            {/* MOTOR */}
            <div className="ss-bet-card motor-card">
              <div className="ss-bet-header">MOTOR</div>
              <form className="ss-bet-form" onSubmit={(e) => { e.preventDefault(); placeMotorBet(); }}>
                <div className="ss-checkbox-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={motorTypes.SP && motorTypes.DP}
                      onChange={(e) => setMotorTypes({ SP: e.target.checked, DP: e.target.checked })}
                    /> ALL
                  </label>
                  {['SP', 'DP'].map((k) => (
                    <label key={k}>
                      <input type="checkbox" checked={!!motorTypes[k]} onChange={() => setMotorTypes((prev) => ({ ...prev, [k]: !prev[k] }))} /> {k}
                    </label>
                  ))}
                </div>
                <div className="ss-form-row motor-row">
                  <div className="ss-input-group">
                    <span className="input-lbl">PTS</span>
                    <input type="number" value={motorInput.pts} onChange={(e) => setMotorInput({ ...motorInput, pts: e.target.value })} />
                  </div>
                  <button type="submit" className="ss-btn-bet">BET</button>
                </div>
                <div className="motor-keys-grid" title="Pick digits (SP needs 3+, DP needs 2+)">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((n) => (
                    <div
                      key={n}
                      className={`motor-key${motorInput.num.includes(n) ? ' active' : ''}`}
                      onClick={() => setMotorInput((prev) => ({ ...prev, num: prev.num.includes(n) ? prev.num.replace(n, '') : prev.num + n }))}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              </form>
            </div>

            {/* game switch */}
            <div className="bottom-category-tabs">
              <div className="cat-tab tab-4d" onClick={() => switchGame('/2d')}>4D</div>
              <div className="cat-tab tab-3d" onClick={() => switchGame('/3d')}>3D</div>
              <div className="cat-tab tab-2d" onClick={() => switchGame('/1d')}>2D</div>
              <div className="cat-tab tab-4d" onClick={() => switchGame('/titli')}>Titli</div>
            </div>
          </div>

          {/* MIDDLE: actions, meta, slip, footer */}
          <div className="middle-board-panel">
            <div className="ss-utility-row" style={{ gridTemplateColumns: `repeat(${ACTIONS.length}, 1fr)` }}>
              {ACTIONS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  className="util-btn"
                  style={{ background: a.color }}
                  onClick={() => onAction(a.label)}
                >
                  {a.label === 'Adv. Draw' && advanceTimes.length > 0 ? `${a.label} (${advanceTimes.length})` : a.label}
                </button>
              ))}
            </div>

            <div className="ss-meta-row">
              <div className="meta-item">{currentDraw}</div>
              <div className="meta-item">GAME ID <span>{currentDrawInfo?.game_id || '—'}</span></div>
              <div className="meta-item">ID BAL <span>{Number(wallet || 0).toLocaleString()}</span></div>
              <div className="meta-item">DATE : <span>{moment().format('DD-MM-YYYY')}</span></div>
              <div className="meta-item">TIME: <span>{clock}</span></div>
              <div className="meta-item">Remain time <span style={{ color: "red", fontWeight: "bold" }}>{remainTime}</span></div>
            </div>

            <div className="ledger-box-outer">
              <div className="ledger-box-inner">
                {bets.length === 0 ? (
                  <div className="ledger-empty">Slip is empty. Place bets on left.</div>
                ) : (
                  bets.map((b) => (
                    <div className="ledger-item" key={b.id}>
                      <span>{b.type} {b.number}x{b.points}</span>
                      <span className="ledger-item-del" title="Remove" onClick={() => removeBet(b.id)}>×</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="ss-footer-actions-row">
              {BOTTOM_BUTTONS
                .filter((b) => {
                  if (b.label === "Whatsapp") return userDetail?.whatsapp;
                  if (b.label === "Print") return userDetail?.print;
                  if (b.label === "Claim") return userDetail?.auto_claim === false;
                  if (b.label === "Bet") return userDetail?.user_bet;

                  // Total Amount hamesha dikhe
                  return true;
                })
                .map((b) =>
                  b.label === "Claim" ? (
                    <input
                      key={b.label}
                      className="footer-btn f-claim"
                      type="text"
                      placeholder="Claim Ticket ID"
                      value={claimTicketId}
                      onChange={(e) => setClaimTicketId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          claimTicket();
                        }

                        if (e.code === "Space") {
                          e.preventDefault();
                          e.target.select();
                        }
                      }}
                      style={{
                        background: "#fff",
                        color: "#000",
                        textAlign: "center",
                        outline: "none",
                      }}
                    />
                  ) : (
                    <button
                      key={b.label}
                      type="button"
                      className={`footer-btn ${{
                        "Total Amount": "f-total",
                        Print: "f-print",
                        Bet: "f-bet",
                        Whatsapp: "f-whatsapp",
                      }[b.label] || ""
                        }`}
                      onClick={() => onBottom(b.label)}
                    >
                      {b.label === "Total Amount"
                        ? `Total : ${grandTotal}`
                        : b.label}
                    </button>
                  )
                )}
              {showWinRate && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.6)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10000,
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      width: "360px",
                      borderRadius: "10px",
                      overflow: "hidden",
                      boxShadow: "0 8px 25px rgba(0,0,0,.3)",
                    }}
                  >
                    <div
                      style={{
                        background: "#0d6efd",
                        color: "#fff",
                        padding: "12px",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      PLAY WIN RATES
                    </div>

                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        textAlign: "center",
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f5f5f5" }}>
                          <th style={{ border: "1px solid #ddd", padding: "10px" }}>Play</th>
                          <th style={{ border: "1px solid #ddd", padding: "10px" }}>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>SP</td>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>10 → 1500</td>
                        </tr>
                        <tr>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>DP</td>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>10 → 3000</td>
                        </tr>
                        <tr>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>TP</td>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>10 → 9000</td>
                        </tr>
                        <tr>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>JODI</td>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                            10 → 900 <br />
                            <small>(Open &amp; Close)</small>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>SINGLE</td>
                          <td style={{ border: "1px solid #ddd", padding: "10px" }}>10 → 90</td>
                        </tr>
                      </tbody>
                    </table>

                    <div style={{ padding: "15px", textAlign: "center" }}>
                      <button
                        onClick={() => setShowWinRate(false)}
                        style={{
                          background: "#dc3545",
                          color: "#fff",
                          border: "none",
                          padding: "8px 25px",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: draw schedule */}
          <div className="right-schedule-panel">
            {slotRows.map((row, i) => {
              const openSlot = { gameId: row.gameId, mode: 'open', time: row.open };
              const closeSlot = { gameId: row.gameId, mode: 'close', time: row.close };
              const openSel = isSlotSelected(openSlot);
              const closeSel = isSlotSelected(closeSlot);
              return (
                <div className="sch-time-row" key={i}>
                  <label className={`sch-checkbox-container${openSel ? ' selected' : ''}${row.openDone ? ' closed' : ''}`}>
                    <input
                      type="checkbox"
                      disabled={!row.gameId || row.openDone}
                      checked={openSel}
                      onChange={() => toggleSlot(openSlot)}
                    />
                    <span className="lbl-time">{row.open}</span>
                  </label>
                  <label className={`sch-checkbox-container${closeSel ? ' selected' : ''}${row.closeDone ? ' closed' : ''}`}>
                    <input
                      type="checkbox"
                      disabled={!row.gameId || row.closeDone}
                      checked={closeSel}
                      onChange={() => toggleSlot(closeSlot)}
                    />
                    <span className="lbl-time">{row.close}</span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* /spreadsheet-container — popups render outside it so the scoped reset can't affect them */}

      <SuccessMessagePopup />
      <ErrorMessagePopup />

      {/* one-page receipt for a just-placed bet (all advance draws stacked, like 3D) */}
      <PanaPrintReceipts tickets={printTickets} onPrinted={() => setPrintTickets([])} />

      {showWhatsapp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '20px 30px', borderRadius: '10px', width: '300px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '15px' }}>Enter Mobile Number</h3>
            <input
              type="tel"
              placeholder="Enter mobile number"
              value={mobileNumber}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= 10 && /^\d*$/.test(v)) setMobileNumber(v);
              }}
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', marginBottom: '15px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <button
                style={{ backgroundColor: '#28a745', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer' }}
                onClick={() => {
                  if (mobileNumber.trim().length < 10) {
                    toast.error('Please enter a valid 10-digit mobile number');
                    return;
                  }
                  placeMatkaBet(false, mobileNumber);
                  setShowWhatsapp(false);
                  setMobileNumber('');
                }}
              >
                Submit
              </button>
              <button
                style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer' }}
                onClick={() => { setShowWhatsapp(false); setMobileNumber(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Outlet />
    </>
  );
}

export default LayoutPana;